import React, { useEffect, useState } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { AlertCircle, RefreshCw, X } from "lucide-react";

interface ServiceWorkerUpdateProps {
  onUpdate?: () => void;
}

export const ServiceWorkerUpdate: React.FC<ServiceWorkerUpdateProps> = ({ onUpdate }) => {
  const [showUpdate, setShowUpdate] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [newVersion, setNewVersion] = useState<string>('');

  useEffect(() => {
    if (!('serviceWorker' in navigator)) {
      return;
    }

    // Listen for service worker updates
    const handleMessage = (event: MessageEvent) => {
      if (event.data && event.data.type === 'SW_UPDATED') {
        console.log('[App] Service Worker updated to version:', event.data.version);
        setNewVersion(event.data.version);
        setShowUpdate(true);
      }
    };

    navigator.serviceWorker.addEventListener('message', handleMessage);

    // Check for waiting service worker
    navigator.serviceWorker.ready.then((registration) => {
      if (registration.waiting) {
        console.log('[App] Service Worker is waiting');
        setShowUpdate(true);
      }

      // Listen for new service worker
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (newWorker) {
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              console.log('[App] New service worker installed');
              setShowUpdate(true);
            }
          });
        }
      });
    });

    return () => {
      navigator.serviceWorker.removeEventListener('message', handleMessage);
    };
  }, []);

  const handleUpdate = async () => {
    setIsUpdating(true);
    
    try {
      // Tell service worker to skip waiting
      if (navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({ type: 'SKIP_WAITING' });
      }

      // Wait a bit for service worker to take control
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Force reload the page
      window.location.reload();
    } catch (error) {
      console.error('[App] Update failed:', error);
      setIsUpdating(false);
    }
  };

  const handleDismiss = () => {
    setShowUpdate(false);
  };

  const clearCacheAndReload = async () => {
    setIsUpdating(true);
    
    try {
      // Clear service worker cache
      if (navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({ type: 'CLEAR_CACHE' });
      }
      
      // Clear browser cache
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map(name => caches.delete(name))
        );
      }
      
      // Force reload
      window.location.reload();
    } catch (error) {
      console.error('[App] Cache clear failed:', error);
      setIsUpdating(false);
    }
  };

  if (!showUpdate) {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 max-w-sm">
      <Card className="border-orange-200 bg-orange-50 shadow-lg">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-orange-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-orange-800">
                Phiên bản mới có sẵn
              </p>
              <p className="text-xs text-orange-600 mt-1">
                {newVersion ? `Version ${newVersion}` : 'Cập nhật ứng dụng để có trải nghiệm tốt nhất'}
              </p>
              <div className="flex gap-2 mt-3">
                <Button
                  onClick={handleUpdate}
                  disabled={isUpdating}
                  size="sm"
                  className="h-7 text-xs bg-orange-600 hover:bg-orange-700"
                >
                  {isUpdating ? (
                    <>
                      <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                      Đang cập nhật...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="h-3 w-3 mr-1" />
                      Cập nhật
                    </>
                  )}
                </Button>
                <Button
                  onClick={clearCacheAndReload}
                  disabled={isUpdating}
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                >
                  Xóa cache
                </Button>
              </div>
            </div>
            <Button
              onClick={handleDismiss}
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 text-orange-600 hover:text-orange-800"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ServiceWorkerUpdate; 